import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  Receipt as SalesImportIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import SalesImportDeprecationBanner from '../../../../components/venue/SalesImportDeprecationBanner';
import salesImportService from '../../../../services/salesImportService';
import type {
  SalesImportLine,
  SalesImportLineStatus,
  SalesImportUploadResponse,
} from '../../../../types/salesImport';

const LINE_STATUS_LABEL: Record<SalesImportLineStatus, string> = {
  pending: 'Ready',
  posted: 'Posted',
  skipped: 'Skipped',
  failed: 'Failed',
};

const LINE_STATUS_COLOR: Record<
  SalesImportLineStatus,
  'default' | 'success' | 'warning' | 'error'
> = {
  pending: 'success',
  posted: 'success',
  skipped: 'warning',
  failed: 'error',
};

const triggerCsvDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const SalesImportUpload: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const base = `/admin/venues/${venueId}/sales-imports`;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // INVENTORY-TRUST.1c — confirm the double-depletion risk before an admin uploads.
  const [confirmUploadOpen, setConfirmUploadOpen] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SalesImportUploadResponse | null>(null);

  const extractErr = (err: any): string =>
    err?.response?.data?.error?.message ??
    err?.response?.data?.message ??
    err?.message ??
    'Unexpected error';

  const handleDownloadTemplate = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await salesImportService.template(venueId);
      triggerCsvDownload(blob, 'manual-sales-import-template.csv');
    } catch (err: any) {
      setError(extractErr(err));
    } finally {
      setDownloading(false);
    }
  }, [venueId]);

  const handleFileSelected = useCallback(
    async (file: File) => {
      setUploading(true);
      setError(null);
      try {
        const res = await salesImportService.upload(venueId, file);
        setPreview(res);
      } catch (err: any) {
        setError(extractErr(err));
      } finally {
        setUploading(false);
        // Reset the input so the same file can be re-selected.
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [venueId],
  );

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFileSelected(f);
  };

  const summary = preview?.summary;
  const importDoc = preview?.salesImport;

  // Summary cards row
  const renderSummaryCards = () => {
    if (!summary) return null;
    const cards: Array<{ label: string; value: number; tone?: 'error' | 'warning' | 'success' }> = [
      { label: 'Total rows', value: summary.rowCount },
      { label: 'Ready', value: summary.pendingLineCount, tone: 'success' },
      { label: 'Skipped', value: summary.skippedLineCount, tone: 'warning' },
      { label: 'Failed', value: summary.failedLineCount, tone: 'error' },
    ];
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}
      >
        {cards.map((c) => {
          const tone =
            c.tone === 'error'
              ? theme.palette.error.main
              : c.tone === 'warning'
                ? theme.palette.warning.main
                : c.tone === 'success'
                  ? theme.palette.success.main
                  : theme.palette.text.primary;
          return (
            <Paper
              key={c.label}
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                flex: 1,
                borderRadius: 2,
                borderColor:
                  c.tone && c.value > 0 ? alpha(tone, 0.4) : alpha(theme.palette.divider, 0.5),
                bgcolor: c.tone && c.value > 0 ? alpha(tone, 0.05) : 'transparent',
              }}
            >
              <Typography
                variant="caption"
                sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 600 }}
              >
                {c.label}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: c.tone && c.value > 0 ? tone : 'text.primary',
                  mt: 0.5,
                }}
              >
                {c.value}
              </Typography>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  const renderPreviewTable = (lines: SalesImportLine[]) => {
    // Sort failed rows to the top so the operator's first eye-stop covers the
    // rows that need attention. Within each group keep the original rowIndex
    // order so the source-file row numbers still climb monotonically.
    const sorted = [...lines].sort((a, b) => {
      if (a.status === 'failed' && b.status !== 'failed') return -1;
      if (b.status === 'failed' && a.status !== 'failed') return 1;
      return a.rowIndex - b.rowIndex;
    });
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Row', 'Item', 'Qty', 'Mode', 'Status', 'Issue'].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((l) => {
              const isFailed = l.status === 'failed';
              const isSkipped = l.status === 'skipped';
              const itemName = l.displayItemName?.trim() || l.posMenuItemRef;
              const showRefBeneath = !!l.displayItemName?.trim();
              return (
                <TableRow
                  key={l._id}
                  sx={{
                    bgcolor: isFailed
                      ? alpha(theme.palette.error.main, 0.08)
                      : isSkipped
                        ? alpha(theme.palette.warning.main, 0.04)
                        : undefined,
                    borderLeft: isFailed
                      ? `3px solid ${theme.palette.error.main}`
                      : '3px solid transparent',
                  }}
                >
                  <TableCell sx={{ width: 56 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      {l.rowIndex}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ lineHeight: 1.3, color: isFailed ? 'error.main' : 'text.primary' }}
                    >
                      {itemName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
                      {l.displayServingSize && (
                        <Chip
                          label={l.displayServingSize}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 500 }}
                        />
                      )}
                      {showRefBeneath && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                        >
                          {l.posMenuItemRef}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 64 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {l.quantitySold}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 80 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {l.resolution?.mode ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 90 }}>
                    <Chip
                      size="small"
                      label={LINE_STATUS_LABEL[l.status]}
                      color={LINE_STATUS_COLOR[l.status]}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    {isFailed ? (
                      <Box>
                        <Typography variant="body2" color="error.main" fontWeight={500}>
                          {l.failureMessage ?? l.failureCode ?? 'Failed'}
                        </Typography>
                        {l.failureCode && (
                          <Tooltip title="Failure code (for support)">
                            <Typography
                              variant="caption"
                              color="error.dark"
                              sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}
                            >
                              {l.failureCode}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!admin) {
    // Non-admins reach this only by direct URL — uploads are retired for them.
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <IconButton onClick={() => navigate(base)} size="small">
            <BackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={600}>Import Sales</Typography>
        </Box>
        <SalesImportDeprecationBanner sx={{ mb: 2 }} />
        <Button variant="outlined" onClick={() => navigate(base)} sx={{ textTransform: 'none' }}>
          Back to Sales Imports
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <IconButton onClick={() => navigate(base)} size="small">
          <BackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          Import Sales
        </Typography>
      </Box>

      <SalesImportDeprecationBanner isAdmin sx={{ mb: 3 }} />

      {/* Step 1 + 2 actions */}
      <Paper sx={{ borderRadius: 2, mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <SalesImportIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Upload a sales file
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              1. Download Template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              The template lists every POS mapping configured for this venue. Fill in quantity sold
              per row, then upload.
            </Typography>
            <Button
              variant="outlined"
              startIcon={
                downloading ? <CircularProgress size={16} /> : <DownloadIcon />
              }
              onClick={handleDownloadTemplate}
              disabled={downloading}
              sx={{ textTransform: 'none' }}
            >
              Download Template
            </Button>
          </Box>
          <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              2. Upload File
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Accepts .csv or .xlsx. We'll validate every row and show a preview before anything is
              posted to inventory.
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              style={{ display: 'none' }}
              onChange={onFileInputChange}
            />
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              onClick={() => setConfirmUploadOpen(true)}
              disabled={uploading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Upload File
            </Button>
          </Box>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Step 3: preview */}
      {preview && importDoc && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Preview — draft saved
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Source: {importDoc.sourceFilename || 'file'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setPreview(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                Upload another
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate(`${base}/${importDoc._id}`)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Open Draft
              </Button>
            </Stack>
          </Box>

          {renderSummaryCards()}

          {summary && summary.failedLineCount > 0 && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error" variant="filled">
                <strong>
                  {summary.failedLineCount} {summary.failedLineCount === 1 ? 'row' : 'rows'} failed.
                </strong>{' '}
                This import cannot be posted. Delete it and upload a corrected file.
              </Alert>
            </Box>
          )}

          {renderPreviewTable(preview.lines)}
        </Paper>
      )}

      {/* INVENTORY-TRUST.1c — double-depletion confirmation before upload */}
      <Dialog open={confirmUploadOpen} onClose={() => setConfirmUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload a manual sales file?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Manual sales import is retired — sales are recorded automatically through the POS
            integration.
            <Box component="p" sx={{ mt: 1.5, mb: 0, fontWeight: 600, color: 'error.main' }}>
              If this file covers a period the POS already recorded, posting it will deplete that
              stock a second time.
            </Box>
            <Box component="p" sx={{ mt: 1.5, mb: 0 }}>
              Only continue for a period the POS did not cover (e.g. a recovery/backfill).
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmUploadOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setConfirmUploadOpen(false); fileInputRef.current?.click(); }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            I understand — choose file
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesImportUpload;
